import dayjs from 'dayjs'
import { IExerciseRecords } from 'hooks/useExerciseRecords'
import { FC, useMemo, useState } from 'react'
import { LuCalendarDays, LuClock } from 'react-icons/lu'
import { BarChart } from 'components/BarChart'
import { LineChart } from 'components/LineChart'
import { ChartBox, Section, SectionHeading } from 'components/ui/Section'
import { Segmented } from 'components/ui/Segmented'
import { ChartSkeleton, Skeleton } from 'components/ui/States'
import { argMax, formatHourRange, formatInt, groupByDay, groupByHour, groupByWeekday, WEEKDAY_LABELS } from 'utils/stats'

type IDailyMetric = 'sessions' | 'minutes'

interface ITimeSection {
    data: IExerciseRecords[]
    from: string
    to: string
    range: string
    loading: boolean
}

interface IBusiestStrip {
    weekday: number[]
    hourly: { hours: number[], counts: number[] }
    loading: boolean
}

/** H4: busiest weekday and hour. */
const BusiestStrip: FC<IBusiestStrip> = ({ weekday, hourly, loading }) => {
    const day = argMax(weekday)
    const hourIndex = argMax(hourly.counts)
    const hour = hourIndex >= 0 ? hourly.hours[hourIndex] : undefined
    const dayText = day >= 0 ? `星期${WEEKDAY_LABELS[day]}` : undefined
    const hourText = hour != null ? formatHourRange(hour) : undefined
    const combined = dayText && hourText ? `${dayText} · ${hourText}` : '—'

    return <div className='card grid grid-cols-1 divide-y divide-line-soft sm:grid-cols-3 sm:divide-x sm:divide-y-0'>
        <div className='p-4 sm:p-5'>
            <p className='text-sm font-medium text-ink-muted'>最繁忙時段</p>
            {loading ? <Skeleton className='mt-2 h-7 w-44' /> : <p className='mt-1 text-xl font-semibold tracking-tight text-ink tabular-nums'>{combined}</p>}
        </div>
        <div className='flex items-center gap-3 p-4 sm:p-5'>
            <span className='grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-50 text-primary'><LuCalendarDays aria-hidden className='h-4 w-4' /></span>
            <div>
                <p className='text-sm font-medium text-ink-muted'>最繁忙星期</p>
                <p className='font-semibold text-ink tabular-nums'>{dayText ? `${dayText} · ${formatInt(weekday[day])} 次` : '—'}</p>
            </div>
        </div>
        <div className='flex items-center gap-3 p-4 sm:p-5'>
            <span className='grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary-50 text-primary'><LuClock aria-hidden className='h-4 w-4' /></span>
            <div>
                <p className='text-sm font-medium text-ink-muted'>最繁忙時間</p>
                <p className='font-semibold text-ink tabular-nums'>{hourText ? `${hourText} · ${formatInt(hourly.counts[hourIndex])} 次` : '—'}</p>
            </div>
        </div>
    </div>
}

/** Section H: when is the equipment used. */
export const TimeSection: FC<ITimeSection> = ({ data, from, to, range, loading }) => {
    const [dailyMetric, setDailyMetric] = useState<IDailyMetric>('sessions')
    const daily = useMemo(() => groupByDay(data, from, to), [data, from, to])
    const dailyCategories = useMemo(() => daily.map((d) => dayjs(d.day).format('DD/MM')), [daily])
    const dailyTooltips = useMemo(() => daily.map((d) => `${dayjs(d.day).format('DD/MM/YYYY')} (星期${WEEKDAY_LABELS[(dayjs(d.day).day() + 6) % 7]})`), [daily])
    const weekday = useMemo(() => groupByWeekday(data), [data])
    const hourly = useMemo(() => groupByHour(data), [data])

    const busiestDay = argMax(weekday)
    const busiestHourIndex = argMax(hourly.counts)

    return <div className='space-y-4 lg:space-y-6'>
        <SectionHeading title='使用時段' description='了解設施的使用時間，方便安排課堂' range={range} />

        <BusiestStrip weekday={weekday} hourly={hourly} loading={loading} />

        <Section
            title='每日運動趨勢'
            subtitle={`${dailyMetric === 'sessions' ? '次數' : '運動時間 · 分鐘'} · ${range}`}
            actions={<Segmented<IDailyMetric>
                ariaLabel='每日趨勢指標'
                options={[{ value: 'sessions', label: '次數' }, { value: 'minutes', label: '分鐘' }]}
                value={dailyMetric}
                onChange={setDailyMetric} />}>
            {loading
                ? <ChartSkeleton />
                : <ChartBox>
                    <LineChart
                        categories={dailyCategories}
                        tooltipLabels={dailyTooltips}
                        series={[dailyMetric === 'sessions'
                            ? { name: '次數', data: daily.map((d) => d.sessions) }
                            : { name: '運動時間', data: daily.map((d) => d.minutes) }]}
                        unit={dailyMetric === 'sessions' ? '次' : '分鐘'}
                        yTitle={dailyMetric === 'sessions' ? '次數' : '分鐘'} />
                </ChartBox>}
        </Section>

        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6'>
            <Section title='星期分佈' subtitle={`次數 · ${range}`}>
                {loading
                    ? <ChartSkeleton />
                    : <ChartBox>
                        <BarChart
                            categories={WEEKDAY_LABELS}
                            tooltipLabels={WEEKDAY_LABELS.map((d) => `星期${d}`)}
                            series={[{ name: '次數', data: weekday }]}
                            highlightIndex={busiestDay >= 0 ? busiestDay : undefined}
                            unit='次'
                            xTitle='星期'
                            yTitle='次數' />
                    </ChartBox>}
            </Section>
            <Section title='時段分佈' subtitle={`次數 · 按小時 · ${range}`}>
                {loading
                    ? <ChartSkeleton />
                    : <ChartBox>
                        <BarChart
                            categories={hourly.hours.map((h) => `${h}`)}
                            tooltipLabels={hourly.hours.map(formatHourRange)}
                            series={[{ name: '次數', data: hourly.counts }]}
                            highlightIndex={busiestHourIndex >= 0 ? busiestHourIndex : undefined}
                            unit='次'
                            xTitle='時 (24小時制)'
                            yTitle='次數' />
                    </ChartBox>}
            </Section>
        </div>
    </div>
}
